/**
 * Currency formatting for the photobooth UI.
 *
 * Mirrors the contract in `dreambooth/lib/billing/geo.ts`. Currency on a
 * `project.resolvedCurrency`, `package.currency`, or `promo.currency` is the
 * write-time stamp from the operator's geography — historical data renders in
 * its original currency forever, regardless of the device's locale.
 *
 * Symbols are explicit (not `Intl.NumberFormat`'s defaults) for widely
 * recognised non-ISO short forms: notably "Rp" for IDR, "RM" for MYR,
 * "S$" for SGD, "NZ$" for NZD. Everything not in the explicit map falls
 * back to `Intl.NumberFormat`, which yields the locale-correct symbol
 * (€, £, ¥, kr, zł, …) and ultimately the ISO code.
 *
 * The kiosk used to type `LocalCurrency` as just the original SEA / India
 * shortlist (`IDR | MYR | THB | SGD | PHP | INR | USD`); any other code
 * was normalised down to IDR via the switch's `default` arm, so an NZD or
 * JPY project rendered "Rp" everywhere on the kiosk despite the dashboard
 * showing the right symbol. The union now mirrors the backend's wider
 * country → currency table and `normalizeCurrency` passes through any
 * valid 3-letter ISO code unchanged, falling back to IDR only for empty /
 * null inputs (legacy projects pre-currency-stamping).
 */
export type LocalCurrency =
  // SEA + India (original)
  | 'USD' | 'IDR' | 'MYR' | 'THB' | 'SGD' | 'PHP' | 'INR'
  // Major worldwide
  | 'EUR' | 'GBP' | 'JPY' | 'CHF' | 'CAD' | 'AUD' | 'NZD'
  // East Asia
  | 'CNY' | 'HKD' | 'TWD' | 'KRW' | 'MOP' | 'MNT'
  // SEA / South Asia extended
  | 'VND' | 'BDT' | 'PKR' | 'LKR' | 'NPR' | 'BTN' | 'MMK' | 'KHR' | 'LAK' | 'BND' | 'MVR'
  // Middle East
  | 'SAR' | 'AED' | 'QAR' | 'KWD' | 'BHD' | 'OMR' | 'JOD' | 'LBP' | 'ILS' | 'IRR' | 'IQD' | 'TRY' | 'YER' | 'SYP'
  // Africa
  | 'EGP' | 'ZAR' | 'NGN' | 'KES' | 'MAD' | 'TND' | 'DZD' | 'GHS' | 'ETB' | 'UGX' | 'TZS' | 'RWF' | 'XOF' | 'XAF'
  // Latin America
  | 'BRL' | 'MXN' | 'ARS' | 'CLP' | 'COP' | 'PEN' | 'UYU' | 'BOB' | 'PYG' | 'VES' | 'GTQ' | 'CRC' | 'DOP' | 'PAB' | 'HNL' | 'NIO'
  // Europe non-EUR
  | 'PLN' | 'CZK' | 'HUF' | 'RON' | 'BGN' | 'HRK' | 'RSD' | 'UAH' | 'RUB' | 'BYN' | 'MDL' | 'ALL' | 'MKD' | 'BAM' | 'ISK' | 'NOK' | 'SEK' | 'DKK'
  // CIS / Central Asia / Caucasus
  | 'KZT' | 'UZS' | 'TJS' | 'TMT' | 'KGS' | 'AZN' | 'GEL' | 'AMD'

export const DEFAULT_CURRENCY: LocalCurrency = 'IDR'

/**
 * Hand-picked symbols for currencies with widely recognised non-ISO short
 * forms (Rp, RM, S$, NZ$, ฿, ₱, ₹). Anything not listed falls back to
 * `Intl.NumberFormat` which yields the locale-correct symbol (€, £, ¥,
 * kr, zł, …) and ultimately the ISO code. Matches the dashboard's
 * `currencySymbol()` helper so what operators see in the dashboard is
 * what customers see on the kiosk.
 */
const SYMBOLS: Partial<Record<LocalCurrency, string>> = {
  USD: '$',
  IDR: 'Rp',
  MYR: 'RM',
  THB: '฿',
  SGD: 'S$',
  PHP: '₱',
  INR: '₹',
  EUR: '€',
  GBP: '£',
  JPY: '¥',
  CNY: '¥',
  HKD: 'HK$',
  TWD: 'NT$',
  KRW: '₩',
  AUD: 'A$',
  CAD: 'C$',
  NZD: 'NZ$',
  CHF: 'CHF',
  SAR: 'SAR',
  AED: 'AED',
  QAR: 'QAR',
  KWD: 'KWD',
  BHD: 'BHD',
  OMR: 'OMR',
  JOD: 'JOD',
  ILS: '₪',
  TRY: '₺',
  ZAR: 'R',
  BRL: 'R$',
  MXN: 'Mex$',
  RUB: '₽',
  PLN: 'zł',
  CZK: 'Kč',
  HUF: 'Ft',
  RON: 'lei',
  SEK: 'kr',
  NOK: 'kr',
  DKK: 'kr'
}

/**
 * Per-currency number-formatting locale. Falls back to `en-US` for codes
 * without an explicit entry — `Intl.NumberFormat` still formats correctly
 * with the right thousands / decimal separators; we just lose the
 * locale-native digit grouping for a few obscure cases.
 */
const NUMBER_LOCALE: Partial<Record<LocalCurrency, string>> = {
  USD: 'en-US',
  IDR: 'id-ID',
  MYR: 'en-MY',
  THB: 'en-TH',
  SGD: 'en-SG',
  PHP: 'en-PH',
  INR: 'en-IN',
  EUR: 'en-IE',
  GBP: 'en-GB',
  JPY: 'ja-JP',
  CNY: 'zh-CN',
  HKD: 'en-HK',
  TWD: 'zh-TW',
  KRW: 'ko-KR',
  AUD: 'en-AU',
  CAD: 'en-CA',
  NZD: 'en-NZ',
  CHF: 'de-CH',
  VND: 'vi-VN',
  PLN: 'pl-PL',
  CZK: 'cs-CZ',
  HUF: 'hu-HU',
  RON: 'ro-RO',
  SEK: 'sv-SE',
  NOK: 'nb-NO',
  DKK: 'da-DK',
  RUB: 'ru-RU',
  TRY: 'tr-TR',
  BRL: 'pt-BR',
  MXN: 'es-MX',
  ZAR: 'en-ZA'
}

/**
 * Currencies conventionally written without a fractional unit. Includes
 * IDR / PHP (the kiosk's no-cents convention since launch) plus the ISO
 * "minor unit = 0" currencies (JPY, KRW, VND, CLP, UGX, …) so they render
 * "¥1,200" rather than "¥1,200.00".
 */
const ZERO_DECIMAL: ReadonlySet<string> = new Set([
  'IDR', 'PHP', 'JPY', 'KRW', 'VND', 'CLP', 'PYG', 'UGX', 'RWF', 'XOF', 'XAF',
  'KHR', 'LAK', 'MMK', 'IRR', 'IQD', 'BIF', 'DJF', 'GNF', 'KMF', 'XPF'
])

const KNOWN_CODES: ReadonlySet<string> = new Set([
  // SEA + India
  'USD','IDR','MYR','THB','SGD','PHP','INR',
  // Major worldwide
  'EUR','GBP','JPY','CHF','CAD','AUD','NZD',
  // East Asia
  'CNY','HKD','TWD','KRW','MOP','MNT',
  // SEA / South Asia extended
  'VND','BDT','PKR','LKR','NPR','BTN','MMK','KHR','LAK','BND','MVR',
  // Middle East
  'SAR','AED','QAR','KWD','BHD','OMR','JOD','LBP','ILS','IRR','IQD','TRY','YER','SYP',
  // Africa
  'EGP','ZAR','NGN','KES','MAD','TND','DZD','GHS','ETB','UGX','TZS','RWF','XOF','XAF',
  // Latin America
  'BRL','MXN','ARS','CLP','COP','PEN','UYU','BOB','PYG','VES','GTQ','CRC','DOP','PAB','HNL','NIO',
  // Europe non-EUR
  'PLN','CZK','HUF','RON','BGN','HRK','RSD','UAH','RUB','BYN','MDL','ALL','MKD','BAM','ISK','NOK','SEK','DKK',
  // CIS / Central Asia / Caucasus
  'KZT','UZS','TJS','TMT','KGS','AZN','GEL','AMD'
])

/**
 * Coerce an incoming string into a `LocalCurrency`. Any 3-letter ISO code
 * we know about passes through verbatim; empty / unknown values fall back
 * to IDR (the original kiosk default — legacy projects predating the
 * currency stamping still need to render in rupiah). New entries here
 * must also be added to the `LocalCurrency` union above.
 */
export function normalizeCurrency(
  input: string | null | undefined
): LocalCurrency {
  const code = (input || '').trim().toUpperCase()
  if (KNOWN_CODES.has(code)) return code as LocalCurrency
  return DEFAULT_CURRENCY
}

/**
 * Render-ready symbol for a currency. Hand-picked overrides for currencies
 * with widely recognised short forms; everything else falls back to
 * `Intl.NumberFormat`'s `currencyDisplay: 'symbol'` (locale-correct €, £,
 * ¥, kr, zł, …) and ultimately the ISO code.
 */
export function currencySymbol(currency: LocalCurrency): string {
  const explicit = SYMBOLS[currency]
  if (explicit) return explicit
  try {
    const parts = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      currencyDisplay: 'symbol'
    }).formatToParts(0)
    const sym = parts.find((p) => p.type === 'currency')?.value
    if (sym) return sym
  } catch {
    // fall through to ISO code
  }
  return currency
}

/** Minor-unit decimals for a currency (0 for IDR/PHP/JPY/KRW/…, 2 otherwise). */
export function currencyDecimals(
  currency: LocalCurrency | string | null | undefined
): number {
  return ZERO_DECIMAL.has(normalizeCurrency(typeof currency === 'string' ? currency : null))
    ? 0
    : 2
}

/**
 * Round a monetary amount to its currency's smallest unit: whole units for
 * IDR/PHP/JPY/KRW/…, 2 decimals otherwise. Use anywhere a bare
 * `Math.round` would otherwise drop ringgit/dollar cents from a subtotal
 * or discount.
 */
export function roundMoney(
  amount: number | null | undefined,
  currency: LocalCurrency | string | null | undefined
): number {
  const value = Number.isFinite(amount as number) ? (amount as number) : 0
  if (currencyDecimals(currency) === 0) return Math.round(value)
  return Math.round(value * 100) / 100
}

export interface FormatAmountOptions {
  /** Force decimal digits. Defaults to 0 for IDR/PHP/JPY/KRW/…, 2 for others. */
  decimals?: number
  /** When true, drops the symbol and returns just the number (e.g. "25.000"). */
  omitSymbol?: boolean
}

/**
 * Format an amount in its stamped currency. Always returns the symbol the
 * dashboard would render (e.g. `Rp 25.000`, `RM 15.00`, `NZ$ 12.50`,
 * `¥ 1,200`).
 */
export function formatAmount(
  amount: number | null | undefined,
  currency: LocalCurrency | string | null | undefined,
  options: FormatAmountOptions = {}
): string {
  const safe = Number.isFinite(amount as number) ? (amount as number) : 0
  const ccy = normalizeCurrency(typeof currency === 'string' ? currency : null)
  const decimals = options.decimals ?? (ZERO_DECIMAL.has(ccy) ? 0 : 2)
  const locale = NUMBER_LOCALE[ccy] ?? 'en-US'

  let body: string
  try {
    body = new Intl.NumberFormat(locale, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    }).format(safe)
  } catch {
    body = safe.toFixed(decimals)
  }

  if (options.omitSymbol) return body
  return `${currencySymbol(ccy)}${body}`
}
